import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar, MapPin, Clock, Ticket, Loader2, Mail, Bell, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, query, orderBy, onSnapshot, addDoc } from 'firebase/firestore';

export default function Shows() {
  const [shows, setShows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscribeError, setSubscribeError] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubscribeError('Please enter a valid email address');
      return;
    }

    setIsSubscribing(true);
    setSubscribeError(null);

    try {
      await addDoc(collection(db, 'showNotifications'), {
        email: email.toLowerCase().trim(),
        createdAt: new Date(),
        source: 'shows_page',
      });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      setSubscribeError('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  useEffect(() => {
    if (!db) {
      setIsLoading(false);
      return;
    }

    const q = query(collection(db, 'shows'), orderBy('date', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const showsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setShows(showsData);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  // Separate upcoming and past shows
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingShows = shows.filter((show) => {
    if (!show.date) return false;
    const showDate = new Date(show.date);
    return showDate >= today && show.status !== 'cancelled';
  });

  const pastShows = shows.filter((show) => {
    if (!show.date) return false;
    const showDate = new Date(show.date);
    return showDate < today || show.status === 'completed';
  }).slice(0, 5); // Only show last 5 past shows

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return { month: 'TBA', day: '??', year: '' };
    const date = /^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))
      ? (() => { const [y,m,d] = String(dateStr).split('-').map(Number); return new Date(y, m-1, d); })()
      : new Date(dateStr);
    return {
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      day: date.getDate().toString(),
      year: date.getFullYear().toString(),
    };
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <motion.section
        className="py-32 px-6 bg-gradient-to-b from-[#0a0a0a] to-black"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <Calendar className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-orange-500">Live Shows</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Experience the energy of go-go music live
          </p>
        </div>
      </motion.section>

      {/* Upcoming Shows */}
      <section className="py-20 px-6 bg-black">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center">Upcoming Shows</h2>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : upcomingShows.length > 0 ? (
            <motion.div
              className="max-w-4xl mx-auto space-y-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.1 }
                }
              }}
            >
              {upcomingShows.map((show) => {
                const { month, day, year } = formatDate(show.date);
                return (
                  <motion.div
                    key={show.id}
                    className="bg-[#1a1a1a] rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between hover:bg-[#252525] transition-colors gap-6"
                    variants={fadeInUp}
                    whileHover={{ x: 10 }}
                  >
                    <div className="flex items-center gap-6 flex-1">
                      <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl p-4 text-center min-w-[80px]">
                        <div className="text-sm font-bold">{month}</div>
                        <div className="text-3xl font-bold">{day}</div>
                        <div className="text-xs">{year}</div>
                      </div>
                      <div className="text-center md:text-left">
                        <h3 className="text-2xl font-bold mb-1">{show.title || show.venue}</h3>
                        <p className="text-orange-400 font-medium">{show.venue}</p>
                        <div className="flex flex-col md:flex-row items-center md:items-start gap-2 md:gap-4 text-gray-400 mt-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{show.city}, {show.state}</span>
                          </div>
                          {show.time && (
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{show.time}</span>
                            </div>
                          )}
                          {show.ticketPrice && (
                            <div className="flex items-center gap-1">
                              <Ticket className="w-4 h-4" />
                              <span>${show.ticketPrice}</span>
                            </div>
                          )}
                        </div>
                        {show.status === 'soldout' && (
                          <span className="inline-block mt-2 px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                            SOLD OUT
                          </span>
                        )}
                      </div>
                    </div>
                    {show.ticketUrl && show.status !== 'soldout' ? (
                      <a href={show.ticketUrl} target="_blank" rel="noopener noreferrer" className="w-full md:w-auto">
                        <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-8 w-full">
                          Get Tickets
                        </Button>
                      </a>
                    ) : show.status !== 'soldout' ? (
                      <Button
                        disabled
                        variant="outline"
                        className="border-gray-700 text-gray-500 w-full md:w-auto"
                      >
                        Tickets Coming Soon
                      </Button>
                    ) : null}
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              className="max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Coming Soon Card */}
              <div className="bg-gradient-to-br from-orange-500/10 to-red-600/10 border border-orange-500/30 rounded-3xl p-8 md:p-12 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Bell className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4">New Shows Coming Soon!</h3>
                <p className="text-gray-400 text-lg mb-8">
                  We're working on bringing you incredible live performances. Be the first to know when new shows are announced!
                </p>

                {/* Email Signup Form */}
                {subscribed ? (
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-6 flex items-center justify-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                    <span className="text-green-400 font-semibold">You're on the list! We'll notify you when new shows are announced.</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                      <div className="relative flex-1">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <Input
                          type="email"
                          placeholder="Enter your email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-12 py-6 bg-gray-900 border-gray-700 text-white placeholder:text-gray-500 rounded-full"
                          disabled={isSubscribing}
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubscribing}
                        className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-8 py-6 rounded-full whitespace-nowrap"
                      >
                        {isSubscribing ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          'Notify Me'
                        )}
                      </Button>
                    </div>
                    {subscribeError && (
                      <p className="text-red-400 text-sm">{subscribeError}</p>
                    )}
                    <p className="text-gray-500 text-sm">We respect your privacy. Unsubscribe anytime.</p>
                  </form>
                )}

                <div className="mt-8 pt-8 border-t border-gray-800">
                  <p className="text-gray-400 mb-4">Want a private performance?</p>
                  <Link to="/contact">
                    <Button variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white rounded-full px-8">
                      Book a Private Event
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Past Shows */}
      {pastShows.length > 0 && (
        <motion.section
          className="py-20 px-6 bg-[#0a0a0a]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold mb-12 text-center">Past Performances</h2>
            <div className="space-y-4">
              {pastShows.map((show) => (
                <div
                  key={show.id}
                  className="bg-[#1a1a1a] rounded-lg p-4 flex items-center justify-between"
                >
                  <div>
                    <h3 className="text-lg font-semibold">{show.title || show.venue}</h3>
                    <p className="text-gray-400 text-sm">{show.venue} - {show.city}, {show.state}</p>
                  </div>
                  <span className="text-gray-500 text-sm">
                    {show.date ? new Date(show.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    }) : 'TBA'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>
      )}

      {/* Email Signup Section - Always show if there are upcoming shows */}
      {upcomingShows.length > 0 && !subscribed && (
        <motion.section
          className="py-16 px-6 bg-[#0a0a0a]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <div className="container mx-auto max-w-2xl">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 text-center border border-gray-700">
              <Bell className="w-10 h-10 text-orange-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Never Miss a Show</h3>
              <p className="text-gray-400 mb-6">Get notified when new shows are announced in your area.</p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 py-5 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 rounded-full"
                    disabled={isSubscribing}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubscribing}
                  className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-bold px-6 py-5 rounded-full"
                >
                  {isSubscribing ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Subscribe'}
                </Button>
              </form>
              {subscribeError && <p className="text-red-400 text-sm mt-2">{subscribeError}</p>}
            </div>
          </div>
        </motion.section>
      )}

      {/* Subscribed Confirmation */}
      {subscribed && upcomingShows.length > 0 && (
        <motion.section
          className="py-8 px-6 bg-[#0a0a0a]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="container mx-auto max-w-2xl">
            <div className="bg-green-500/20 border border-green-500/30 rounded-xl p-4 flex items-center justify-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-green-400">You're subscribed! We'll email you about new shows.</span>
            </div>
          </div>
        </motion.section>
      )}

      {/* Booking CTA */}
      <motion.section
        className="py-20 px-6 bg-black"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Book a Performance</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Bring the authentic sound of go-go to your event. Contact us for bookings and availability.
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-12 py-4 text-lg rounded-full">
              Book Now
            </Button>
          </Link>
        </div>
      </motion.section>
    </div>
  );
}
